import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Compass, Search, Loader2, ChevronRight, MapPin, Building2, GitBranch, CloudOff, ShieldQuestion, CheckCircle2, XCircle, UploadCloud, ExternalLink } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SeedBadge } from "@/components/common/SeedBadge";
import { RESEARCH_STATUS, OPERATOR_TYPE, formatDate } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Link } from "react-router-dom";

const KPI_DEFS = [
  ["total", "Gesamt gefunden"],
  ["discovered", "Ungeprüft"],
  ["review_required", "Review nötig"],
  ["partially_verified", "Teilw. verifiziert"],
  ["verified", "Verifiziert"],
  ["ready_for_import", "Importbereit"],
  ["rejected", "Abgelehnt"],
  ["imported", "Importiert"],
];

const DUP_RISK = {
  high: { label: "Hoch", cls: "bg-status-danger-bg text-status-danger border-status-danger-border" },
  medium: { label: "Mittel", cls: "bg-status-warning-bg text-status-warning border-status-warning-border" },
  low: { label: "Gering", cls: "bg-fundio-subtle text-fundio-muted border-fundio-border" },
};

const VER_CHECKS = [
  ["address_verified", "Adresse verifiziert"],
  ["contact_verified", "Kontakt verifiziert"],
  ["hours_verified", "Öffnungszeiten verifiziert"],
  ["operator_verified", "Betreiber verifiziert"],
  ["dedup_resolved", "Dedup geklärt"],
];

function RealBadge({ show }) {
  if (!show) return null;
  return <span data-testid="real-badge" title="Echte recherchierte Daten (kein Demo)" className="ml-2 inline-flex items-center rounded border border-status-success-border bg-status-success-bg px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wider text-status-success">REAL DATA</span>;
}

function DupRisk({ possible, confidence, testid }) {
  if (!possible) return <span data-testid={testid} className="text-xs text-fundio-faint">—</span>;
  const r = DUP_RISK[confidence] || DUP_RISK.low;
  return <span data-testid={testid} className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${r.cls}`}>{r.label}</span>;
}

function Field({ label, value }) {
  const empty = value === null || value === undefined || value === "";
  return (
    <div>
      <dt className="text-xs text-fundio-faint">{label}</dt>
      <dd className="text-sm text-fundio-ink break-words">{empty ? <span className="italic text-fundio-faint">Noch nicht verfügbar</span> : String(value)}</dd>
    </div>
  );
}

export default function Research() {
  const { can } = useAuth();
  const writable = can("research", "write");
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState(null);
  const [filters, setFilters] = useState({ canton: "", verification_status: "", operator: "", operator_type: "", q: "" });
  const [selected, setSelected] = useState(null);

  const loadStats = useCallback(() => {
    api.get("/research/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  const loadList = useCallback(() => {
    setItems(null);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/research/candidates", { params }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [filters]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadList(); }, [loadList]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/research/candidates/${id}`);
      setSelected(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const facets = stats?.facets || { cantons: [], operators: [], statuses: [], operator_types: [] };

  return (
    <div data-testid="research-page">
      <PageHeader title="Brocki-Verzeichnis" subtitle="Schweizweites Verzeichnis realer Schweizer Brockis (Research/Directory). REAL DATA – KEINE FUNDIO-Kunden, kein Abo, kein Login." />

      {/* Research-Data banner */}
      <div data-testid="research-scope-note" className="mb-5 flex items-center gap-2 rounded-lg border border-status-info-border bg-status-info-bg/50 px-4 py-2 text-xs text-status-info">
        <ShieldQuestion className="h-4 w-4" />
        <span><span className="font-semibold">RESEARCH DATA</span> · Öffentlich recherchierte Standorte zur Prüfung. Kein Kundenkonto, kein Abo, kein kanonischer Import in dieser Ansicht.</span>
      </div>

      {/* KPIs */}
      <div data-testid="research-kpis" className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {KPI_DEFS.map(([k, label]) => (
          <div key={k} data-testid={`research-kpi-${k}`} className="card-surface rounded-xl px-4 py-3">
            <p className="text-xs text-fundio-muted">{label}</p>
            <p className="mt-1 font-display text-2xl font-semibold text-fundio-ink">{stats ? stats.kpis[k] : "—"}</p>
          </div>
        ))}
      </div>

      <Panel testid="research-panel" noPad
        title="Research Candidates"
        actions={
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fundio-faint" />
            <Input data-testid="research-search" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Name, Ort, PLZ, Website, Betreiber…" className="input-dark w-64 pl-9" />
          </div>
        }>
        {/* Filters */}
        <div className="flex flex-wrap gap-2 border-b border-fundio-border px-4 py-3">
          <select data-testid="research-filter-canton" value={filters.canton} onChange={(e) => setFilters({ ...filters, canton: e.target.value })} className="rounded-lg border border-fundio-border bg-white px-3 py-1.5 text-sm text-fundio-ink outline-none focus:border-fundio-gold">
            <option value="">Alle Kantone</option>
            {facets.cantons.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="research-filter-status" value={filters.verification_status} onChange={(e) => setFilters({ ...filters, verification_status: e.target.value })} className="rounded-lg border border-fundio-border bg-white px-3 py-1.5 text-sm text-fundio-ink outline-none focus:border-fundio-gold">
            <option value="">Alle Status</option>
            {facets.statuses.map((s) => <option key={s} value={s}>{RESEARCH_STATUS[s]?.label || s}</option>)}
          </select>
          <select data-testid="research-filter-operator" value={filters.operator} onChange={(e) => setFilters({ ...filters, operator: e.target.value })} className="rounded-lg border border-fundio-border bg-white px-3 py-1.5 text-sm text-fundio-ink outline-none focus:border-fundio-gold">
            <option value="">Alle Betreiber</option>
            {facets.operators.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <select data-testid="research-filter-operator-type" value={filters.operator_type} onChange={(e) => setFilters({ ...filters, operator_type: e.target.value })} className="rounded-lg border border-fundio-border bg-white px-3 py-1.5 text-sm text-fundio-ink outline-none focus:border-fundio-gold">
            <option value="">Unabhängig / Filiale</option>
            {facets.operator_types.map((t) => <option key={t} value={t}>{OPERATOR_TYPE[t]?.label || t}</option>)}
          </select>
        </div>

        {items === null ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>
        ) : items.length === 0 ? (
          <div className="p-4"><EmptyState icon={Compass} title="Keine Kandidaten" description="Für die aktuellen Filter wurden keine Research Candidates gefunden." testid="research-empty" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-fundio-border bg-fundio-subtle text-left text-xs font-semibold uppercase tracking-wider text-fundio-muted">
                  <th className="px-5 py-3">Brocki</th><th className="px-5 py-3">Ort</th><th className="px-5 py-3">Kanton</th>
                  <th className="px-5 py-3">Betreiber</th><th className="px-5 py-3">Typ</th><th className="px-5 py-3">Dubletten-Risiko</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-fundio-border">
                {items.map((c) => (
                  <tr key={c.id} data-testid={`research-row-${c.id}`} onClick={() => openDetail(c.id)} className="cursor-pointer bg-white transition-colors hover:bg-fundio-subtle">
                    <td className="px-5 py-3.5 font-medium text-fundio-ink">{c.name}<SeedBadge show={c.is_seed} /><RealBadge show={!c.is_seed} /></td>
                    <td className="px-5 py-3.5 text-fundio-muted"><span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-fundio-faint" />{c.city || "—"}</span></td>
                    <td className="px-5 py-3.5 font-mono text-xs text-fundio-muted">{c.canton || "—"}</td>
                    <td className="px-5 py-3.5 text-fundio-muted">{c.operator_guess || <span className="italic text-fundio-faint">—</span>}</td>
                    <td className="px-5 py-3.5"><StatusBadge map={OPERATOR_TYPE} value={c.operator_type} testid={`research-optype-${c.id}`} /></td>
                    <td className="px-5 py-3.5"><DupRisk possible={c.possible_duplicate} confidence={c.operator_match_confidence} testid={`research-duprisk-${c.id}`} /></td>
                    <td className="px-5 py-3.5"><StatusBadge map={RESEARCH_STATUS} value={c.verification_status} testid={`research-status-${c.id}`} /></td>
                    <td className="px-5 py-3.5 text-right"><span className="inline-flex items-center gap-1 text-sm font-semibold text-fundio-ink">Öffnen <ChevronRight className="h-4 w-4" /></span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <ResearchDetail candidate={selected} onClose={() => setSelected(null)} writable={writable}
        statuses={facets.statuses}
        onChanged={(c) => { setSelected(c); loadList(); loadStats(); }} />
    </div>
  );
}

function ResearchDetail({ candidate, onClose, writable, statuses, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  useEffect(() => { setPreview(null); }, [candidate?.id]);
  if (!candidate) return null;
  const c = candidate;
  const q = c.quality || { completeness: 0, verification: {} };

  const setStatus = async (verification_status) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/research/candidates/${c.id}/status`, { verification_status });
      toast.success("Status aktualisiert");
      onChanged(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/research/candidates/${c.id}/import-preview`);
      setPreview(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/research/candidates/${c.id}/import`);
      toast.success(`Offiziell in FUNDIO übernommen (tenant ${data.tenant_id})`);
      setPreview(null);
      const fresh = await api.get(`/research/candidates/${c.id}`);
      onChanged(fresh.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Sheet open={!!candidate} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="research-detail-sheet" className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex flex-wrap items-center gap-2 font-display text-fundio-ink">
            {c.name}<SeedBadge show={c.is_seed} /><RealBadge show={!c.is_seed} />
            <StatusBadge map={RESEARCH_STATUS} value={c.verification_status} />
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <div data-testid="research-detail-scope" className="flex items-center gap-2 rounded-lg border border-status-info-border bg-status-info-bg/50 px-3 py-2 text-xs text-status-info">
            <ShieldQuestion className="h-4 w-4" /><span><span className="font-semibold">RESEARCH DATA</span> · KEIN FUNDIO-Kunde</span>
          </div>

          <div className="card-surface rounded-xl p-4">
            <div className="mb-3 text-sm font-semibold text-fundio-ink">Standortdaten</div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <Field label="Name" value={c.name} />
              <Field label="Adresse" value={c.street} />
              <Field label="PLZ" value={c.postal_code} />
              <Field label="Ort" value={c.city} />
              <Field label="Kanton" value={c.canton} />
              <Field label="Land" value={c.country} />
              <Field label="Telefon" value={c.phone} />
              <Field label="E-Mail" value={c.email} />
              <Field label="Website" value={c.website} />
              <Field label="Öffnungszeiten" value={c.opening_hours} />
            </dl>
          </div>

          <div className="card-surface rounded-xl p-4">
            <div className="mb-3 text-sm font-semibold text-fundio-ink">Recherche & Betreiber</div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <Field label="Discovery-Quelle" value={c.source_url} />
              <Field label="Offizielle Quelle" value={c.official_website} />
              <Field label="Betreiber-Vermutung" value={c.operator_guess} />
              <div><dt className="text-xs text-fundio-faint">Typ</dt><dd className="mt-0.5"><StatusBadge map={OPERATOR_TYPE} value={c.operator_type} /></dd></div>
              <Field label="Dedup-Gruppe" value={c.dedup_group} />
              <Field label="Letzter Prüfzeitpunkt" value={formatDate(c.last_checked_at)} />
            </dl>
            <div data-testid="research-dedup-block" className="mt-3 border-t border-fundio-border pt-3">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-fundio-faint">Dubletten-Risiko:</span>
                <DupRisk possible={c.possible_duplicate} confidence={c.operator_match_confidence} testid="research-detail-duprisk" />
              </div>
              <p className="mt-1.5 text-xs text-fundio-muted">{c.dedup_reason || "—"}</p>
              <p className="mt-1 text-[11px] text-fundio-faint">Web-Domain: {c.website_domain || "—"} · E-Mail-Domain: {c.email_domain || "—"}</p>
            </div>
            {(c.canonical_tenant_id || c.canonical_location_id) && (
              <p className="mt-3 border-t border-fundio-border pt-3 text-xs text-fundio-muted">
                Kanonisch: tenant_id {c.canonical_tenant_id || "—"} · location_id {c.canonical_location_id || "—"}
              </p>
            )}
          </div>

          <div className="card-surface rounded-xl p-4" data-testid="research-quality-card">
            <div className="mb-3 text-sm font-semibold text-fundio-ink">Datenqualität</div>
            <div className="mb-3">
              <div className="flex justify-between text-xs text-fundio-muted"><span>Vollständigkeit</span><span className="font-mono" data-testid="research-completeness">{q.completeness}%</span></div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-fundio-border"><div className="h-full rounded-full bg-fundio-gold" style={{ width: `${q.completeness}%` }} /></div>
            </div>
            <p className="mb-2 text-[11px] text-fundio-faint">Verifikation (unabhängig von der Vollständigkeit):</p>
            <ul className="space-y-1.5" data-testid="research-quality-checks">
              {VER_CHECKS.map(([k, label]) => (
                <li key={k} className="flex items-center gap-2 text-xs">
                  {q.verification?.[k] ? <CheckCircle2 className="h-3.5 w-3.5 text-status-success" /> : <XCircle className="h-3.5 w-3.5 text-status-danger" />}
                  <span className={q.verification?.[k] ? "text-fundio-muted" : "text-status-danger"}>{label}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Status transitions */}
          {writable && (
            <div className="card-surface rounded-xl p-4">
              <div className="mb-3 text-sm font-semibold text-fundio-ink">Verifikationsstatus setzen</div>
              <div className="flex flex-wrap gap-2">
                {(statuses || []).filter((s) => s !== "IMPORTED").map((s) => (
                  <Button key={s} data-testid={`research-setstatus-${s}`} disabled={busy || c.verification_status === s}
                    onClick={() => setStatus(s)}
                    className="border border-fundio-border bg-white text-xs font-medium text-fundio-ink hover:border-fundio-gold disabled:opacity-40">
                    {RESEARCH_STATUS[s]?.label || s}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Offiziell in FUNDIO übernehmen (Phase R4-B) */}
          {c.verification_status === "IMPORTED" ? (
            <div className="card-surface rounded-xl p-4" data-testid="research-imported-block">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-status-success"><CheckCircle2 className="h-4 w-4" />Offiziell in FUNDIO übernommen</div>
              <ul className="ml-1 space-y-1 text-xs text-fundio-muted">
                <li>tenant_id <span className="font-mono text-fundio-ink" data-testid="research-imported-tenant">{c.canonical_tenant_id || "—"}</span></li>
                <li>location_id <span className="font-mono text-fundio-ink" data-testid="research-imported-location">{c.canonical_location_id || "—"}</span></li>
              </ul>
              {c.canonical_tenant_id && (
                <Link to={`/brockis/${c.canonical_tenant_id}`} data-testid="research-canonical-link"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-fundio-gold hover:underline">
                  Kanonischen Eintrag öffnen <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              )}
              <p className="mt-2 text-[11px] text-fundio-faint">Directory-Import: erscheint im kanonischen Verzeichnis, aber NICHT als zahlender FUNDIO-Kunde (customer_status = directory_only · claim_status = unclaimed · Plan = FREE).</p>
            </div>
          ) : writable && c.verification_status === "READY_FOR_IMPORT" ? (
            <div className="card-surface rounded-xl p-4" data-testid="research-import-block">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-sm font-semibold text-fundio-ink">Offiziell in FUNDIO übernehmen</div>
                <Button data-testid="research-import-open-button" disabled={busy} onClick={runPreview}
                  className="border border-fundio-gold bg-fundio-dark2 text-xs font-semibold text-fundio-cream hover:border-fundio-gold-highlight">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="inline-flex items-center gap-1"><UploadCloud className="h-4 w-4" />Übernehmen</span>}
                </Button>
              </div>
              {preview === null ? (
                <p className="text-xs text-fundio-muted">Zeigt eine Bestätigungsansicht mit dem geplanten kanonischen Ergebnis. Erst nach Bestätigung wird geschrieben.</p>
              ) : !preview.service_available ? (
                <div data-testid="research-import-unavailable" className="flex items-center gap-2 text-xs text-status-danger"><CloudOff className="h-4 w-4" />{preview.reason}</div>
              ) : (
                <div data-testid="research-import-preview" className="space-y-3 text-sm">
                  <div className="rounded-lg border border-fundio-border bg-fundio-subtle p-3">
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-fundio-muted">Research Candidate</div>
                    <p className="text-sm font-medium text-fundio-ink">{preview.candidate?.name}</p>
                    <p className="text-xs text-fundio-muted">{[preview.candidate?.street, `${preview.candidate?.postal_code || ""} ${preview.candidate?.city || ""}`.trim(), preview.candidate?.canton].filter(Boolean).join(" · ")}</p>
                    <p className="mt-1 flex flex-wrap items-center gap-1 text-xs text-fundio-muted">Betreiber: <span className="text-fundio-ink">{preview.candidate?.operator_guess || "—"}</span> · <StatusBadge map={OPERATOR_TYPE} value={preview.candidate?.operator_type} />{preview.candidate?.dedup_group ? <span>· Dedup: {preview.candidate.dedup_group}</span> : null}</p>
                    <p className="mt-1 text-[11px] text-fundio-faint break-words">Quelle: {preview.candidate?.official_source || "—"}</p>
                  </div>
                  <div className="rounded-lg border border-fundio-border p-3">
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-fundio-muted">Geplantes kanonisches Ergebnis</div>
                    <ul className="space-y-1 text-xs text-fundio-muted">
                      <li className="flex items-center gap-2">{preview.is_chain ? <GitBranch className="h-3.5 w-3.5 text-status-warning" /> : <Building2 className="h-3.5 w-3.5 text-status-info" />}<span>{preview.is_chain ? "Kette / Filiale" : "Unabhängig"} · account_mode = <span className="font-semibold text-fundio-ink">{preview.account_mode}</span></span></li>
                      <li data-testid="research-import-org-plan">Organization: {preview.organization?.exists
                        ? <span className="text-fundio-ink">bestehende wiederverwenden <span className="font-mono">{preview.organization.reused_tenant_id}</span></span>
                        : <span className="text-fundio-ink">neu anlegen „{preview.organization?.company_name}"</span>}</li>
                      <li data-testid="research-import-loc-plan">Location: <span className="text-fundio-ink">{preview.location?.would_create ? "neue Location hinzufügen" : `bestehende Location ${preview.location?.existing_location_id}`}</span></li>
                      <li className="pt-1">Commercial: <span className="font-mono text-fundio-ink">directory_only</span> · <span className="font-mono text-fundio-ink">unclaimed</span> · Plan <span className="font-mono text-fundio-ink">FREE</span></li>
                      <li className="text-[11px] text-fundio-faint break-all">Idempotency: {preview.idempotency?.source_system} / {preview.idempotency?.source_record_id}{preview.is_chain ? ` · Ketten-Dedup: ${preview.idempotency?.dedup_group}` : ""}</li>
                    </ul>
                  </div>
                  <div className="rounded-lg border border-status-warning-border bg-status-warning-bg/40 p-2 text-[11px] text-status-warning">Kein Kundenkonto, kein Login, keine Membership, kein Vertrag, keine E-Mail. Auch MULTI-Ketten bleiben directory_only (kein zahlender Kunde).</div>
                  <div className="flex items-center gap-2">
                    <Button data-testid="research-import-confirm-button" disabled={busy || !preview.ready} onClick={doImport}
                      className="bg-fundio-gold text-xs font-semibold text-fundio-dark2 hover:bg-fundio-gold-highlight disabled:opacity-40">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Übernahme bestätigen"}
                    </Button>
                    <Button data-testid="research-import-cancel-button" disabled={busy} onClick={() => setPreview(null)}
                      className="border border-fundio-border bg-white text-xs font-medium text-fundio-ink hover:border-fundio-gold">Abbrechen</Button>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <p className="text-[11px] text-fundio-faint">Angelegt {formatDate(c.created_at)} · zuletzt geändert {formatDate(c.updated_at)}</p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
